import { useEffect, useState } from "react";
import StaffingModal from "./StaffingModal";
import { DataTable, ColumnDef } from "../../common/DataTable";
import { Badge } from "../../ui/badge";
import { Posting } from "@/types/staffing/posting";
import { getPostings } from "@/services/staffing/Staffing";
import { useApiClient } from "@/context/ApiContext";
import { getStatusBadgeColor } from "@/utils/staffingUtil";

interface ClientPostingsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientId: number;
    clientName: string | null;
}

const columns: ColumnDef<Posting>[] = [
    {
        key: 'Id',
        header: 'ID',
        sortable: true
    },
    {
        key: 'Status',
        header: 'Status',
        sortable: true,
        render: (row) => <Badge className={getStatusBadgeColor(row.Status)}>{row.Status}</Badge>
    },
    {
        key: 'ServiceType',
        header: 'Service Type',
        sortable: true
    },
    {
        key: 'StaffedProvider',
        header: 'Staffed Provider',
        sortable: false,
        render: (row) => {
            const provider = row.Processes.find(x => x.Status === 'Staffed');
            return provider ? provider.Staff : 'Not Staffed';
        }
    },
    {
        key: 'Telehealth',
        header: 'Telehealth',
        sortable: true,
        render: (row) => row.Telehealth ? 'Yes' : 'No'
    },
    {
        key: 'Created',
        header: 'Created',
        sortable: true,
        render: (row) => new Date(row.Created).toLocaleDateString()
    },
];

const ClientPostingsModal = ({ open, onOpenChange, clientId, clientName }: ClientPostingsModalProps) => {
    const apiClient = useApiClient();
    const [postings, setPostings] = useState<Posting[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        getPostings({ filter: `ClientId eq ${clientId}`, expand: ['processes'], orderby: 'Id desc' }, apiClient)
            .then(res => {
                const raw = res.data.value ?? [];
                setPostings(raw.map((p: Posting & { Reasons: string | null }) => ({
                    ...p,
                    Reasons: p.Reasons ? JSON.parse(p.Reasons) : null
                })));
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [open, clientId, apiClient]);

    return (
        <StaffingModal open={open} onOpenChange={onOpenChange} title={`Postings — ${clientName}`} wide>
            <DataTable
                data={postings}
                columns={columns}
                loading={loading}
            />
        </StaffingModal>
    );
};

export default ClientPostingsModal;
